/*
 * Ported from mstreetinvest@cec8be916:resources/js/components/crm-chat/cards/format.ts (PORTING.md §1.2).
 * Edits (plan Ruling 7): times are read in the admin's own zone — the browser's, as on every console page
 * (lib/admin/time.ts) — instead of mStreet's business zone; counts group en-GB, and money with no currency is a
 * plain en-GB number rather than Indian grouping. mStreet's short dates and phrases are unchanged.
 */

/*
 * Formatting shared by the chat cards. Dates that describe work (promise due dates) are read in business time,
 * the same zone the lead board labels them in, so the chat and the board never disagree about "today".
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** The admin's own zone: undefined, so every formatter below reads the browser's (lib/admin/time.ts does the same). */
export const BUSINESS_TIME_ZONE: string | undefined = undefined;

function parseTime(iso: string | null | undefined): number | null {
    if (!iso) {
        return null;
    }

    const time = Date.parse(iso);

    return Number.isNaN(time) ? null : time;
}

const monthDay = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
});
const monthYear = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
});

/** Compact age for row corners: "now", "5m", "2h", "3d", "Sep 12", "Sep 2024". */
export function compactAge(
    iso: string | null | undefined,
    now: number = Date.now(),
): string | null {
    const time = parseTime(iso);

    if (time === null) {
        return null;
    }

    const diff = Math.max(0, now - time);

    if (diff < MINUTE) {
        return 'now';
    }
    if (diff < HOUR) {
        return `${Math.floor(diff / MINUTE)}m`;
    }
    if (diff < DAY) {
        return `${Math.floor(diff / HOUR)}h`;
    }
    if (diff < 7 * DAY) {
        return `${Math.floor(diff / DAY)}d`;
    }

    return new Date(time).getFullYear() === new Date(now).getFullYear()
        ? monthDay.format(time)
        : monthYear.format(time);
}

/** Full timestamp for title attributes: "Thu, Sep 24, 2026, 11:00". */
export function absoluteDateTime(iso: string | null | undefined): string {
    const time = parseTime(iso);

    return time === null
        ? ''
        : new Intl.DateTimeFormat('en-GB', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              timeZone: BUSINESS_TIME_ZONE,
          }).format(time);
}

interface BusinessDate {
    /** Days since the epoch in business time, for day-level comparisons. */
    day: number;
    year: number;
    weekday: string;
    dayOfMonth: string;
    month: string;
    hour: number;
    minute: number;
}

const businessParts = new Intl.DateTimeFormat('en-US', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});

const businessDayKey = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

function toBusinessDate(time: number): BusinessDate {
    const parts = Object.fromEntries(
        businessParts
            .formatToParts(time)
            .map((part) => [part.type, part.value]),
    );

    return {
        day: Math.floor(Date.parse(businessDayKey.format(time)) / DAY),
        year: Number(parts.year),
        weekday: parts.weekday ?? '',
        dayOfMonth: parts.day ?? '',
        month: parts.month ?? '',
        hour: Number(parts.hour),
        minute: Number(parts.minute),
    };
}

export interface PromiseDue {
    text: string;
    overdue: boolean;
}

/**
 * When a promise is due, in the board's words and business time: "Due today", "Due tomorrow 15:00", "Due 12 Oct",
 * "Overdue since yesterday", "Overdue since 17 Jun" (with the year outside the current one). The tool's resolved
 * `due_label` wins when present; the timing phrase from the email ("after 3 PM today") is never shown. Noon is the
 * extractor's default when the email named no time, so only other times are added, and only for today/tomorrow.
 */
export function promiseDue(
    promise: {
        due_label?: string | null;
        due_at?: string | null;
        overdue?: boolean;
    },
    now: number = Date.now(),
): PromiseDue | null {
    const label = promise.due_label?.trim();

    if (label) {
        return { text: label, overdue: /^overdue/i.test(label) };
    }

    const time = parseTime(promise.due_at);

    if (time === null) {
        return promise.overdue ? { text: 'Overdue', overdue: true } : null;
    }

    const due = toBusinessDate(time);
    const today = toBusinessDate(now);
    const offset = due.day - today.day;
    const date =
        due.year === today.year
            ? `${due.dayOfMonth} ${due.month}`
            : `${due.dayOfMonth} ${due.month} ${due.year}`;
    const clock =
        due.hour === 12 && due.minute === 0
            ? ''
            : ` ${String(due.hour).padStart(2, '0')}:${String(due.minute).padStart(2, '0')}`;

    // A promise only turns overdue once its day has passed, as on the board.
    if (offset < 0 && promise.overdue !== false) {
        return {
            text: `Overdue since ${offset === -1 ? 'yesterday' : date}`,
            overdue: true,
        };
    }
    if (offset === 0) {
        return { text: `Due today${clock}`, overdue: false };
    }
    if (offset === 1) {
        return { text: `Due tomorrow${clock}`, overdue: false };
    }

    return { text: `Due ${date}`, overdue: false };
}

const countFormat = new Intl.NumberFormat('en-GB');
const compactFormat = new Intl.NumberFormat('en-GB', {
    notation: 'compact',
    maximumFractionDigits: 1,
});

export function formatCount(value: number): string {
    return countFormat.format(value);
}

/** Axis-friendly numbers: 950, 1.2K, 3.4M. */
export function formatCompact(value: number): string {
    return Math.abs(value) < 1000
        ? countFormat.format(value)
        : compactFormat.format(value);
}

export function plural(count: number, singular: string, many?: string): string {
    return `${formatCount(count)} ${count === 1 ? singular : (many ?? `${singular}s`)}`;
}

/** Minutes as a duration: "45m", "2h 15m", "1d 3h". */
export function formatMinutes(minutes: number): string {
    const rounded = Math.max(0, Math.round(minutes));

    if (rounded < 60) {
        return `${rounded}m`;
    }

    if (rounded < 24 * 60) {
        const hours = Math.floor(rounded / 60);
        const rest = rounded % 60;

        return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
    }

    const days = Math.floor(rounded / (24 * 60));
    const hours = Math.floor((rounded % (24 * 60)) / 60);

    return hours === 0 ? `${days}d` : `${days}d ${hours}h`;
}

/** Money with the currency's own grouping: ₹12L, ₹1.2Cr, $450K; plain numbers when the code is unknown. */
export function formatMoney(value: number, currency?: string | null): string {
    const code = (currency ?? '').trim().toUpperCase();
    const locale = code === 'INR' ? 'en-IN' : code === '' ? 'en-GB' : 'en-US';

    try {
        return new Intl.NumberFormat(locale, {
            style: code === '' ? 'decimal' : 'currency',
            currency: code === '' ? undefined : code,
            notation: Math.abs(value) >= 100_000 ? 'compact' : 'standard',
            maximumFractionDigits: Math.abs(value) >= 100_000 ? 1 : 0,
        }).format(value);
    } catch {
        return `${code} ${countFormat.format(value)}`.trim();
    }
}

/** "site_visit" → "Site visit". */
export function humanize(value: string): string {
    const text = value.replace(/[_-]+/g, ' ').trim();

    return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "http://www.acme.in/about" → "acme.in". */
export function cleanDomain(value: string | null | undefined): string | null {
    if (!value) {
        return null;
    }

    const domain = value
        .trim()
        .replace(/^[a-z]+:\/\//i, '')
        .replace(/^www\./i, '')
        .replace(/[/?#].*$/, '');

    return domain === '' ? null : domain.toLowerCase();
}

/** Two-letter mark for a person, company or domain: "Table Space" → "TS", "Awfis" → "AW", "acme.in" → "AC". */
export function initialsOf(value: string | null | undefined): string {
    const source = (value ?? '').trim().replace(/^[a-z]+:\/\//i, '');
    const base = source.includes(' ')
        ? source
        : (source.replace(/^www\./i, '').split(/[.@]/)[0] ?? source);
    const words = base.split(/[\s._-]+/).filter((word) => /\w/.test(word));

    if (words.length === 0) {
        return '?';
    }

    const letters =
        words.length > 1
            ? `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`
            : words[0].slice(0, 2);

    return letters.toUpperCase();
}

const ISO_DATE = /^\d{4}-\d{2}(-\d{2})?([T ][\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;

export function isIsoDate(value: unknown): value is string {
    return typeof value === 'string' && ISO_DATE.test(value.trim());
}

/** Parses "2026-09-12" as a calendar date (not a UTC instant) so labels never slip a day. */
function calendarDate(value: string): Date | null {
    const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?/.exec(value.trim());

    if (!match) {
        return null;
    }

    return new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        match[3] ? Number(match[3]) : 1,
    );
}

/** Axis tick for a date column: "Sep 12", or "Sep 2026" for month buckets. */
export function formatAxisDate(value: string): string {
    const date = calendarDate(value);

    if (!date) {
        return value;
    }

    return /^\d{4}-\d{2}$/.test(value.trim())
        ? monthYear.format(date)
        : monthDay.format(date);
}

/** Tooltip heading for a date column: "Sat, Sep 12, 2026". */
export function formatLongDate(value: string): string {
    const date = calendarDate(value);

    return date
        ? new Intl.DateTimeFormat('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          }).format(date)
        : value;
}
